import { useCallback, useEffect, useMemo } from 'react'
import { useHistory, useLocation } from 'react-router-dom'

// Global object for storing changes
let searchParamsBuffer = new Map<string, string | null>()
let updateTimeout: NodeJS.Timeout | null = null

/**
 * URL Update Debounce Function
 */
const updateUrl = (history: ReturnType<typeof useHistory>) => {
  if (updateTimeout) clearTimeout(updateTimeout)

  updateTimeout = setTimeout(() => {
    const currentParams = new URLSearchParams(window.location.search)

    searchParamsBuffer.forEach((value, key) => {
      if (value === null) {
        currentParams.delete(key)
      } else {
        currentParams.set(key, value)
      }
    })

    history.replace({ search: currentParams.toString() })
    searchParamsBuffer.clear()
  }, 50)
}

// ------------------------------------
// Options for the useSearchParams hook

interface Options {
  exclusive?: boolean
  initialValue?: string | number
}

interface PreparedSearchParamsArgs {
  key?: string
  options?: Options
}

const prepareSearchParamsArgs = (
  key?: string,
  initialValueOrOptions?: string | number | Options,
  options?: Omit<Options, 'exclusive'>,
): PreparedSearchParamsArgs => {
  if (!key) {
    return { key: undefined, options: undefined }
  }

  let finalOptions: Options | undefined

  if (
    initialValueOrOptions !== undefined &&
    typeof initialValueOrOptions !== 'object'
  ) {
    const initialValue = initialValueOrOptions
    finalOptions = options ? { ...options, initialValue } : { initialValue }
  } else {
    finalOptions = initialValueOrOptions as Options | undefined
  }

  return { key, options: finalOptions }
}

/**
 * Custom hook to manage URL search parameters with debounced updates.
 *
 * @remarks
 * - The hook provides two modes: managing all search parameters or a specific parameter by key.
 * - When used with a key, it supports an `exclusive` option to remove all other parameters when setting a new value.
 * - Updates to the URL are debounced with a 200ms delay to prevent excessive re-renders.
 *
 * @returns
 * - Without key: `[URLSearchParams, (key: string, value: string | number) => void, (key: string) => void]`
 * - With key: `[string | null, (value: string | number) => void, () => void]`
 *
 * @example
 * // Usage without a key
 * const [params, set, remove] = useSearchParams();
 * set('page', 2); // Sets the 'page' search parameter to '2'
 * remove('page'); // Removes the 'page' search parameter
 * params.get('page'); // Returns '2'
 *
 * // Usage with a key
 * const [page, setPage, removePage] = useSearchParams('page');
 * setPage(2); // Sets 'page' to '2'
 * removePage(); // Removes 'page'
 * page; // Returns '2'
 *
 * @param {string} [key] - The key of the search parameter to manage.
 * @param {string | number} [initialValue] - The default value to set if the key does not exist (used in the second overload).
 * @param {Object} [options] - Options for managing the search parameter.
 * @param {boolean} [options.exclusive] - If true, setting a new value removes all other search parameters (used in the third overload).
 * @param {string | number} [options.initialValue] - The default value to set if the key does not exist (used in the third overload).
 * @returns
 * - If `key` is not provided: `[URLSearchParams, (key: string, value: string | number) => void, (key: string) => void]` - Provides access to all search parameters, a setter, and a remover.
 * - If `key` is provided: `[string | null, (value: string | number) => void, () => void]` - Provides the current value of the parameter, a setter, and a remover.
 */

export function useSearchParams(): [
  URLSearchParams,
  (key: string, value: string | number) => void,
  (key: string) => void,
]

export function useSearchParams(
  key: string,
  initialValue?: string | number,
  options?: Omit<Options, 'exclusive'>,
): [string | null, (value: string | number) => void, () => void]

export function useSearchParams(
  key: string,
  options?: Options,
): [string | null, (value: string | number) => void, () => void]

export function useSearchParams(
  ...args: Parameters<typeof prepareSearchParamsArgs>
) {
  const { key, options } = prepareSearchParamsArgs(...args)

  const history = useHistory()
  const location = useLocation()
  const params = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  )

  useEffect(() => {
    if (!key || options?.initialValue === undefined) return

    if (!params.has(key)) {
      searchParamsBuffer.set(key, options.initialValue.toString())
      updateUrl(history)
    }
  }, [key, options?.initialValue, history])

  const set = useCallback(
    (key: string, value: string | number) => {
      // Если exclusive: true, удаляем все остальные параметры
      if (options?.exclusive) {
        const currentParams = new URLSearchParams(window.location.search)
        searchParamsBuffer.clear()
        for (const paramKey of currentParams.keys()) {
          if (paramKey !== key) {
            searchParamsBuffer.set(paramKey, null)
          }
        }
      }

      searchParamsBuffer.set(key, value.toString())
      updateUrl(history)
    },
    [history, options?.exclusive],
  )

  const remove = useCallback(
    (key: string) => {
      searchParamsBuffer.set(key, options?.initialValue?.toString() ?? null)
      updateUrl(history)
    },
    [history],
  )

  if (key) {
    return [
      params.get(key),
      (value: string | number) => set(key, value),
      () => remove(key),
    ] as const
  }

  return [params, set, remove] as const
}